import { Spin } from "antd";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { UserProfileVO } from "src/models/UserProfile";
import { ApiClient, apiClient } from "src/api/client";
import { useUserStore } from "src/stores/UserStore";

interface AuthContextType {
  user: UserProfileVO | null;
  login: (userProfile: UserProfileVO) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  loading: boolean;
  refreshAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserProfileVO | null>(null);
  const [loading, setLoading] = useState(true);
  const setUserInStore = useUserStore((s) => s.setUser);
  const clearUserStore = useUserStore((s) => s.clearUser);

  const handleCleanup = useCallback(() => {
    const theme = localStorage.getItem("theme");
    localStorage.clear();
    if (theme) localStorage.setItem("theme", theme);
    setUser(null);
    clearUserStore();
  }, [clearUserStore]);

  const refreshAuth = useCallback(async (): Promise<boolean> => {
    try {
      const res = await apiClient.post<UserProfileVO | null>("/v1/auth/refresh");
      if (res) {
        setUser(res);
        setUserInStore(res);
        return true;
      }
      handleCleanup();
      return false;
    } catch {
      handleCleanup();
      return false;
    }
  }, [handleCleanup, setUserInStore]);

  const login = useCallback(
    (userProfile: UserProfileVO) => {
      setUser(userProfile);
      setUserInStore(userProfile);
      localStorage.setItem("deviceId", userProfile.deviceId);
    },
    [setUserInStore],
  );

  const logout = useCallback(async () => {
    try {
      const deviceId = localStorage.getItem("deviceId");
      await apiClient.post("/v1/auth/logout", { deviceId });
    } catch {
      // 로그아웃 API 실패는 무시
    } finally {
      handleCleanup();
      window.location.href = "/login";
    }
  }, [handleCleanup]);

  const isAuthenticated = useCallback(() => !!user, [user]);

  useEffect(() => {
    ApiClient.setOnSessionExpired(() => {
      handleCleanup();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login?tokenExpired=true";
      }
    });
  }, [handleCleanup]);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const checkAuth = async () => {
      try {
        const profile = await apiClient.get<UserProfileVO>("/v1/user/profile", undefined, {
          signal: controller.signal,
        });
        if (!mounted) return;
        if (profile) {
          setUser(profile);
          setUserInStore(profile);
        } else {
          setUser(null);
        }
      } catch {
        if (!mounted) return;
        setUser(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    checkAuth();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [setUserInStore]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" tip="Loading...">
          <div style={{ padding: 50, background: "rgba(0,0,0,0.05)", borderRadius: 4 }} />
        </Spin>
      </div>
    );
  }

  const value = { user, login, logout, isAuthenticated, loading, refreshAuth };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
